import React from 'react';
import { Filter, X, Plus, Trash2 } from 'lucide-react';
import { FilterField, FilterOperator, FilterRule } from '../types';
import { LEAD_SOURCES, LEAD_STATES } from '../data';
interface FilterPanelProps {
  rules: FilterRule[];
  onRulesChange: (rules: FilterRule[]) => void;
  totalLeads: number;
  filteredCount: number;
}
interface FieldConfig {
  label: string;
  type: 'text' | 'enum' | 'date';
  options?: string[];
  operators: FilterOperator[];
}
const FIELD_CONFIG: Record<FilterField, FieldConfig> = {
  source: {
    label: 'Source',
    type: 'enum',
    options: LEAD_SOURCES,
    operators: ['equals', 'not_equals', 'is_one_of', 'is_not_one_of']
  },
  state: {
    label: 'State',
    type: 'enum',
    options: LEAD_STATES,
    operators: ['equals', 'not_equals', 'is_one_of', 'is_not_one_of']
  },
  firstName: {
    label: 'First Name',
    type: 'text',
    operators: ['equals', 'not_equals', 'contains', 'not_contains']
  },
  lastName: {
    label: 'Last Name',
    type: 'text',
    operators: ['equals', 'not_equals', 'contains', 'not_contains']
  },
  email: {
    label: 'Email',
    type: 'text',
    operators: ['equals', 'not_equals', 'contains', 'not_contains']
  },
  createdAt: {
    label: 'Created At',
    type: 'date',
    operators: ['before', 'after']
  }
};
const OPERATOR_LABELS: Record<FilterOperator, string> = {
  equals: 'equals',
  not_equals: 'does not equal',
  contains: 'contains',
  not_contains: 'does not contain',
  is_one_of: 'is one of',
  is_not_one_of: 'is not one of',
  before: 'is before',
  after: 'is after'
};
function newRule(): FilterRule {
  return {
    id: Math.random().toString(36).slice(2, 9),
    field: 'source',
    operator: 'equals',
    value: ''
  };
}
export function FilterPanel({
  rules,
  onRulesChange,
  totalLeads,
  filteredCount
}: FilterPanelProps) {
  const updateRule = (id: string, patch: Partial<FilterRule>) => {
    onRulesChange(rules.map(r => {
      if (r.id !== id) return r;
      const updated = {
        ...r,
        ...patch
      };
      // If field changed, reset operator + value to valid defaults
      if (patch.field && patch.field !== r.field) {
        const cfg = FIELD_CONFIG[patch.field];
        updated.operator = cfg.operators[0];
        updated.value = '';
      }
      // If operator changed type (single ↔ multi), reset value
      if (patch.operator && patch.operator !== r.operator) {
        const wasMulti = r.operator === 'is_one_of' || r.operator === 'is_not_one_of';
        const isMulti = patch.operator === 'is_one_of' || patch.operator === 'is_not_one_of';
        if (wasMulti !== isMulti) updated.value = '';
      }
      return updated;
    }));
  };
  const addRule = () => onRulesChange([...rules, newRule()]);
  const removeRule = (id: string) => onRulesChange(rules.filter(r => r.id !== id));
  const clearAll = () => onRulesChange([]);
  const excluded = totalLeads - filteredCount;
  return <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden" data-id="element-55">
      <div className="px-6 py-5 border-b border-slate-200 bg-slate-50/50 flex items-center justify-between" data-id="element-56">
        <div className="flex items-center gap-2" data-id="element-57">
          <Filter className="w-5 h-5 text-slate-500" data-id="element-58" />
          <div data-id="element-59">
            <h2 className="text-lg font-semibold text-slate-900" data-id="element-60">
              1. Filter Leads
            </h2>
            <p className="text-sm text-slate-500 mt-0.5" data-id="element-61">
              Build rules to narrow down which leads to distribute. All rules
              must match (AND).
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3" data-id="element-62">
          <div className="text-right" data-id="element-63">
            <div className="text-2xl font-bold text-indigo-600 tabular-nums" data-id="element-64">
              {filteredCount.toLocaleString()}
            </div>
            <div className="text-xs text-slate-500" data-id="element-65">
              of {totalLeads.toLocaleString()} matched
              {excluded > 0 && <span className="text-slate-400" data-id="element-66">
                  {' '}
                  · {excluded.toLocaleString()} excluded
                </span>}
            </div>
          </div>
          {rules.length > 0 && <button onClick={clearAll} className="text-xs font-medium text-slate-500 hover:text-slate-900 flex items-center gap-1 px-2 py-1 rounded hover:bg-slate-100 transition-colors" data-id="element-67">
              <X className="w-3 h-3" data-id="element-68" />
              Clear
            </button>}
        </div>
      </div>

      <div className="p-6" data-id="element-69">
        {rules.length === 0 ? <div className="text-center py-8 px-4 bg-slate-50 rounded-lg border border-dashed border-slate-300" data-id="element-70">
            <p className="text-slate-600 font-medium mb-1" data-id="element-71">
              No filters applied
            </p>
            <p className="text-slate-500 text-sm mb-4" data-id="element-72">
              All {totalLeads.toLocaleString()} leads will be distributed.
            </p>
            <button onClick={addRule} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-indigo-600 bg-indigo-50 hover:bg-indigo-100 rounded-md transition-colors" data-id="element-73">
              <Plus className="w-4 h-4" data-id="element-74" />
              Add filter
            </button>
          </div> : <div className="space-y-2" data-id="element-75">
            {rules.map((rule, idx) => <RuleRow key={rule.id} rule={rule} isFirst={idx === 0} onUpdate={patch => updateRule(rule.id, patch)} onRemove={() => removeRule(rule.id)} data-id="element-76" />)}
            <button onClick={addRule} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-indigo-600 hover:bg-indigo-50 rounded-md transition-colors mt-2" data-id="element-77">
              <Plus className="w-4 h-4" data-id="element-78" />
              Add filter
            </button>
          </div>}
      </div>
    </div>;
}
interface RuleRowProps {
  rule: FilterRule;
  isFirst: boolean;
  onUpdate: (patch: Partial<FilterRule>) => void;
  onRemove: () => void;
}
function RuleRow({
  rule,
  isFirst,
  onUpdate,
  onRemove
}: RuleRowProps) {
  const config = FIELD_CONFIG[rule.field];
  const isMulti = rule.operator === 'is_one_of' || rule.operator === 'is_not_one_of';
  return <div className="flex items-center gap-2 p-2 rounded-lg bg-slate-50 border border-slate-200" data-id="element-79">
      <div className="w-14 text-xs font-semibold text-slate-500 text-center uppercase tracking-wider" data-id="element-80">
        {isFirst ? 'Where' : 'And'}
      </div>

      {/* Field */}
      <select value={rule.field} onChange={e => onUpdate({
      field: e.target.value as FilterField
    })} className="px-3 py-2 rounded-md border border-slate-200 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500 min-w-[140px]" data-id="element-81">
        {(Object.keys(FIELD_CONFIG) as FilterField[]).map(f => <option key={f} value={f} data-id="element-82">
            {FIELD_CONFIG[f].label}
          </option>)}
      </select>

      {/* Operator */}
      <select value={rule.operator} onChange={e => onUpdate({
      operator: e.target.value as FilterOperator
    })} className="px-3 py-2 rounded-md border border-slate-200 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500 min-w-[150px]" data-id="element-83">
        {config.operators.map(op => <option key={op} value={op} data-id="element-84">
            {OPERATOR_LABELS[op]}
          </option>)}
      </select>

      {/* Value */}
      <div className="flex-grow" data-id="element-85">
        <ValueInput rule={rule} config={config} isMulti={isMulti} onChange={value => onUpdate({
        value
      })} data-id="element-86" />
      </div>

      <button onClick={onRemove} className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors" aria-label="Remove filter" data-id="element-87">
        <Trash2 className="w-4 h-4" data-id="element-88" />
      </button>
    </div>;
}
interface ValueInputProps {
  rule: FilterRule;
  config: FieldConfig;
  isMulti: boolean;
  onChange: (value: string) => void;
}
function ValueInput({
  rule,
  config,
  isMulti,
  onChange
}: ValueInputProps) {
  // Multi-value (comma separated) for is_one_of / is_not_one_of
  if (isMulti) {
    if (config.type === 'enum') {
      const selected = rule.value ? rule.value.split(',').map(s => s.trim()).filter(Boolean) : [];
      const toggle = (opt: string) => {
        const next = selected.includes(opt) ? selected.filter(s => s !== opt) : [...selected, opt];
        onChange(next.join(','));
      };
      return <details className="relative" data-id="element-89">
          <summary className="list-none cursor-pointer px-3 py-2 rounded-md border border-slate-200 text-sm bg-white hover:border-slate-300" data-id="element-90">
            {selected.length === 0 ? <span className="text-slate-400" data-id="element-91">Select values…</span> : <span className="text-slate-700" data-id="element-92">{selected.length} selected</span>}
          </summary>
          <div className="absolute z-20 mt-1 w-64 max-h-64 overflow-y-auto bg-white border border-slate-200 rounded-md shadow-lg p-2" data-id="element-93">
            {config.options?.map(opt => <label key={opt} className="flex items-center gap-2 px-2 py-1.5 hover:bg-slate-50 rounded cursor-pointer text-sm" data-id="element-94">
                <input type="checkbox" checked={selected.includes(opt)} onChange={() => toggle(opt)} className="accent-indigo-600" data-id="element-95" />
                {opt}
              </label>)}
          </div>
        </details>;
    }
    return <input type="text" value={rule.value} onChange={e => onChange(e.target.value)} placeholder="value1, value2, value3" className="w-full px-3 py-2 rounded-md border border-slate-200 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500" data-id="element-96" />;
  }
  // Single value
  if (config.type === 'enum') {
    return <select value={rule.value} onChange={e => onChange(e.target.value)} className="w-full px-3 py-2 rounded-md border border-slate-200 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500" data-id="element-97">
        <option value="" data-id="element-98">Select a value…</option>
        {config.options?.map(opt => <option key={opt} value={opt} data-id="element-99">
            {opt}
          </option>)}
      </select>;
  }
  if (config.type === 'date') {
    // value stored as YYYY-MM-DDTHH:mm
    return <input type="datetime-local" value={rule.value} onChange={e => onChange(e.target.value)} className="w-full px-3 py-2 rounded-md border border-slate-200 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500" data-id="element-100" />;
  }
  return <input type="text" value={rule.value} onChange={e => onChange(e.target.value)} placeholder="Enter a value" className="w-full px-3 py-2 rounded-md border border-slate-200 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500" data-id="element-101" />;
}